/**
 * useAudioInput.ts
 *
 * Manages audio-input device enumeration and selection, mirroring the
 * pattern used by useCamera for video devices.  Does NOT process audio
 * — it only provides the MediaStream and device list so consumers
 * (e.g. usePitchDetection) can create their own AudioContext pipeline.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

/* ------------------------------------------------------------------ */
/*  Public types                                                       */
/* ------------------------------------------------------------------ */

export interface AudioInputState {
  /** Whether a mic stream is currently active */
  isActive: boolean;
  /** Whether we are in the process of starting the mic */
  isLoading: boolean;
  /** Last error message, if any */
  error: string | null;
  /** Current MediaStream (null when inactive) */
  stream: MediaStream | null;
  /** Available audio-input devices */
  devices: MediaDeviceInfo[];
  /** Currently selected device ID (empty string = system default) */
  selectedDeviceId: string;
}

export interface AudioInputActions {
  /** Start capturing from the selected (or default) device */
  start: () => Promise<void>;
  /** Stop capturing and release the stream */
  stop: () => void;
  /** Re-enumerate audio-input devices */
  refreshDevices: () => Promise<void>;
  /** Select a device by ID.  If already active, restarts on the new device. */
  selectDevice: (deviceId: string) => Promise<void>;
}

export interface UseAudioInputReturn {
  state: AudioInputState;
  actions: AudioInputActions;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function stopStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => t.stop());
}

function getAudioErrorMessage(error: unknown): string {
  if (error instanceof DOMException) {
    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'Microphone permission was denied. Allow mic access in the browser and try again.';
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'No microphone was found on this device.';
      case 'NotReadableError':
      case 'TrackStartError':
        return 'The microphone is already in use by another application.';
      default:
        return error.message || 'Failed to access microphone';
    }
  }
  return error instanceof Error ? error.message : 'Failed to access microphone';
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

const AUDIO_DEVICE_STORAGE_KEY = 'jamcam-audio-device-id';

export function loadSavedAudioDeviceId(): string {
  try {
    return localStorage.getItem(AUDIO_DEVICE_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveDeviceId(id: string): void {
  try {
    localStorage.setItem(AUDIO_DEVICE_STORAGE_KEY, id);
  } catch {
    // silently fail
  }
}

export function useAudioInput(enabled = true): UseAudioInputReturn {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const generation = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);

  const [state, setState] = useState<AudioInputState>({
    isActive: false,
    isLoading: false,
    error: null,
    stream: null,
    devices: [],
    selectedDeviceId: loadSavedAudioDeviceId(),
  });

  // ---- device enumeration ----

  const refreshDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = all.filter((d) => d.kind === 'audioinput');
      setState((s) => {
        const stillExists = audioInputs.some(
          (d) => d.deviceId === s.selectedDeviceId
        );
        return {
          ...s,
          devices: audioInputs,
          selectedDeviceId: stillExists
            ? s.selectedDeviceId
            : audioInputs[0]?.deviceId ?? '',
        };
      });
    } catch {
      // Silently ignore — devices will be empty
    }
  }, []);

  // Enumerate on mount + listen for device changes
  useEffect(() => {
    if (!enabled || !navigator.mediaDevices) return;
    void refreshDevices();
    const onChange = () => void refreshDevices();
    navigator.mediaDevices.addEventListener('devicechange', onChange);
    return () =>
      navigator.mediaDevices.removeEventListener('devicechange', onChange);
  }, [refreshDevices, enabled]);

  // ---- start / stop ----

  const stop = useCallback(() => {
    generation.current++;
    stopStream(streamRef.current);
    streamRef.current = null;
    setState((s) => ({
      ...s,
      isActive: false,
      isLoading: false,
      error: null,
      stream: null,
    }));
  }, []);

  useEffect(() => {
    if (!enabled) stop();
    return stop;
  }, [enabled, stop]);

  const start = useCallback(async () => {
    if (!enabledRef.current) return;
    const request = ++generation.current;
    // Tear down any existing stream first
    stopStream(streamRef.current);
    streamRef.current = null;

    setState((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          ...(state.selectedDeviceId
            ? { deviceId: { exact: state.selectedDeviceId } }
            : {}),
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (!enabledRef.current || request !== generation.current) {
        stopStream(stream);
        return;
      }
      streamRef.current = stream;

      setState((s) => ({
        ...s,
        isActive: true,
        isLoading: false,
        error: null,
        stream,
      }));

      // After obtaining permission, labels become available — refresh list
      await refreshDevices();
    } catch (err) {
      if (!enabledRef.current || request !== generation.current) return;
      setState((s) => ({
        ...s,
        isActive: false,
        isLoading: false,
        error: getAudioErrorMessage(err),
        stream: null,
      }));
    }
  }, [state.selectedDeviceId, refreshDevices]);

  const selectDevice = useCallback(async (deviceId: string) => {
    if (!enabledRef.current) return;
    const request = ++generation.current;
    saveDeviceId(deviceId);
    setState((s) => ({ ...s, selectedDeviceId: deviceId }));

    // If currently active, restart on the new device
    if (streamRef.current) {
      stopStream(streamRef.current);
      streamRef.current = null;

      setState((s) => ({ ...s, isLoading: true, error: null }));

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            deviceId: { exact: deviceId },
          },
        });
        if (!enabledRef.current || request !== generation.current) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        setState((s) => ({
          ...s,
          isActive: true,
          isLoading: false,
          error: null,
          stream,
        }));
      } catch (err) {
        if (!enabledRef.current || request !== generation.current) return;
        setState((s) => ({
          ...s,
          isActive: false,
          isLoading: false,
          error: getAudioErrorMessage(err),
          stream: null,
        }));
      }
    }
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, []);

  return {
    state,
    actions: { start, stop, refreshDevices, selectDevice },
  };
}

export default useAudioInput;
